import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { api, isTokenValid, clearTokenFromStorage, triggerLogoutEvent } from '../services/api'

interface Admin {
  id: string
  email: string
  name: string
}

interface AuthContextType {
  admin: Admin | null
  token: string | null
  isLoading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  isAuthenticated: boolean
  checkAuth: () => Promise<boolean>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [admin, setAdmin] = useState<Admin | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const isCheckingRef = useRef(false)
  const tokenRef = useRef<string | null>(null)
  const adminRef = useRef<Admin | null>(null)

  useEffect(() => {
    tokenRef.current = token
  }, [token])

  useEffect(() => {
    adminRef.current = admin
  }, [admin])

  const checkAuth = useCallback(async (): Promise<boolean> => {
    // Evita múltiplas verificações simultâneas
    if (isCheckingRef.current) {
      console.log('[AUTH] Já está verificando, aguardando...')
      return !!tokenRef.current && !!adminRef.current
    }

    isCheckingRef.current = true
    console.log('[AUTH] 🔍 Verificando autenticação...')

    try {
      const storedToken = localStorage.getItem('catalisa_token')
      const storedAdmin = localStorage.getItem('catalisa_admin_data')

      console.log('[AUTH] Token no storage:', storedToken ? '✅ Presente' : '❌ Ausente')
      console.log('[AUTH] Admin no storage:', storedAdmin ? '✅ Presente' : '❌ Ausente')

      if (!storedToken) {
        console.log('[AUTH] ❌ Nenhum token encontrado')
        setToken(null)
        setAdmin(null)
        setIsLoading(false)
        return false
      }

      if (!isTokenValid(storedToken)) {
        console.log('[AUTH] ❌ Token inválido ou expirado')
        clearTokenFromStorage()
        setToken(null)
        setAdmin(null)
        setIsLoading(false)
        triggerLogoutEvent()
        return false
      }

      console.log('[AUTH] Token válido, validando com o backend...')
      const adminData = await api.getMe(storedToken)
      console.log('[AUTH] ✅ Admin autenticado:', adminData.email)
      
      setToken(storedToken)
      setAdmin(adminData)
      setIsLoading(false)
      return true
    } catch (error) {
      console.error('[AUTH] ❌ Erro ao validar token:', error)
      clearTokenFromStorage()
      setToken(null)
      setAdmin(null)
      setIsLoading(false)
      triggerLogoutEvent()
      return false
    } finally {
      isCheckingRef.current = false
    }
  }, [])

  // Verifica autenticação ao carregar o app
  useEffect(() => {
    console.log('[AUTH] Inicializando verificação de autenticação...')
    checkAuth()
  }, []) // ← SÓ EXECUTA UMA VEZ

  // Listener para logout automático por eventos
  useEffect(() => {
    const handleLogoutEvent = () => {
      console.log('[AUTH] Evento de logout recebido')
      clearTokenFromStorage()
      setToken(null)
      setAdmin(null)
      setIsLoading(false)
    }

    window.addEventListener('auth:logout', handleLogoutEvent)

    return () => {
      window.removeEventListener('auth:logout', handleLogoutEvent)
    }
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    console.log('[AUTH] 🔐 Tentando login:', email)
    setIsLoading(true)
    try {
      const response = await api.login(email, password)
      console.log('[AUTH] ✅ Login bem-sucedido')
      
      localStorage.setItem('catalisa_token', response.token)
      localStorage.setItem('catalisa_admin_data', JSON.stringify(response.admin))
      
      setToken(response.token)
      setAdmin(response.admin)
    } catch (error) {
      console.error('[AUTH] ❌ Erro no login:', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }, [])

  const logout = useCallback(async () => {
    console.log('[AUTH] 🔓 Realizando logout...')
    setIsLoading(true)
    try {
      const currentToken = token || localStorage.getItem('catalisa_token')
      if (currentToken) {
        try {
          await api.logout(currentToken)
          console.log('[AUTH] ✅ Logout no backend OK')
        } catch (error) {
          console.error('[AUTH] Erro no logout da API:', error)
        }
      }
    } finally {
      clearTokenFromStorage()
      setToken(null)
      setAdmin(null)
      setIsLoading(false)
      console.log('[AUTH] ✅ Logout concluído')
    }
  }, [token])

  const value = React.useMemo(() => ({
    admin,
    token,
    isLoading,
    login,
    logout,
    checkAuth,
    isAuthenticated: !!token && !!admin && !isLoading
  }), [admin, token, isLoading, login, logout, checkAuth])

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}